import CalculadoraInss from '../CalculadoraInss';
import CalculadoraIrrf from '../CalculadoraIrrf';
import {
  DEDUCAO_DEPENDENTE,
  VALOR_DESCONTO_SIMPLIFICADO,
} from '../CalculadoraIrrf/constants';
import { VALUE_TYPE_PERCENTAGE } from '../repository/settingRepository/objects/setting';
import {
  PERCENTUAL_FGTS,
  PERCENTUAL_PROVISAO_13_TERCEIRO,
  PERCENTUAL_ENCARGOS_13_TERCEIRO,
  PERCENTUAL_FERIAS_TERCO,
  PERCENTUAL_ENCARGOS_FERIAS,
  PERCENTUAL_FGTS_RESCISAO,
} from './constants';

export default class SimuladorRe {
  constructor(fields, aliquotasImposto) {
    this.assignFields(fields);
    this.criarCalculadorasDeImpostos(aliquotasImposto);
  }

  criarCalculadorasDeImpostos(aliquotasImposto = {}) {
    const { faixaInss, tetoInss, faixaIrrf } = aliquotasImposto;
    this.calculadoraInss = new CalculadoraInss(faixaInss, tetoInss);
    this.calculadoraIrrf = new CalculadoraIrrf(faixaIrrf);
  }

  assignFields(fields) {
    Object.entries(fields).forEach(([key, field]) => {
      this[key] = { ...field };
    });
  }

  applySettings() {
    this.applyPercentageSetting('salario', 'remuneracaoBase');
    this.applyPercentageSetting('transporte', 'remuneracaoBase');
    this.applyPercentageSetting('beneficios', 'remuneracaoBase');
    this.applyPercentageSetting('ajudaCusto', 'remuneracaoBase');

    this.applySetting('alimentacaoRefeicao');
    this.applySetting('assistenciaMedica');
    this.applySetting('odontologico');
    this.applySetting('outrosCustos');
    this.applySetting('percentualGps');
  }

  getField(field) {
    return this[field];
  }

  getValue(field) {
    if (this.isVisible(field)) {
      return this[field].value;
    }

    return 0;
  }

  isVisible(field) {
    return this[field] && this[field].isVisible;
  }

  applyPercentageSetting(keyToApplySetting, keyToApplyPercentage) {
    const fieldToApplySetting = this[keyToApplySetting];
    if (!fieldToApplySetting) {
      return;
    }

    const { setting } = fieldToApplySetting;
    if (setting.value === 0) {
      return;
    }

    const fieldToApplyPercentage = this.getValue(keyToApplyPercentage);
    const isPercentage = setting.valueType === VALUE_TYPE_PERCENTAGE;
    if (isPercentage) {
      fieldToApplySetting.value =
        fieldToApplyPercentage * (setting.value / 100);
    } else {
      fieldToApplySetting.value = setting.value;
    }
  }

  applySetting(keyToApplySetting) {
    const fieldToApplySetting = this[keyToApplySetting];
    if (!fieldToApplySetting) {
      return;
    }

    const { setting } = fieldToApplySetting;
    if (setting.value === 0) {
      return;
    }

    fieldToApplySetting.value = setting.value;
  }

  getRemuneracaoTotal() {
    return (
      this.getRemuneracaoBase() + this.getValorAdicional() + this.getPremio()
    );
  }

  getAjudaCusto(isAdministrativo) {
    if (isAdministrativo) {
      const descontos =
        this.getSalario() +
        this.getTransporte() +
        this.getBeneficios() +
        this.getRefeicao() +
        this.getAlimentacao() +
        this.getReembolso() +
        this.getValorAdicional() +
        this.getPremio() +
        this.getOutrosDescontos();

      return this.getRemuneracaoTotal() - descontos;
    }

    return this.getValue('ajudaCusto');
  }

  getPida(isAdministrativo) {
    if (isAdministrativo) {
      return 0;
    }

    const descontos =
      this.getSalario() +
      this.getTransporte() +
      this.getBeneficios() +
      this.getAjudaCusto() +
      this.getAlimentacao() +
      this.getRefeicao() +
      this.getPremio() +
      this.getValorAdicional();

    return this.getRemuneracaoTotal() - descontos;
  }

  getPat() {
    return this.getAlimentacao() + this.getRefeicao();
  }

  getSalario() {
    return this.getValue('salario');
  }

  getOutrosDescontos() {
    return this.getValue('outrosDescontos');
  }

  getReembolso() {
    return this.getValue('reembolso');
  }

  getRemuneracaoBase() {
    return this.getValue('remuneracaoBase');
  }

  getValorAdicional() {
    return this.getValue('valorAdicional');
  }

  getPremio() {
    return this.getValue('premio');
  }

  getTransporte() {
    return this.getValue('transporte');
  }

  getBeneficios() {
    return this.getValue('beneficios');
  }

  getAlimentacao() {
    return this.getValue('alimentacao');
  }

  getRefeicao() {
    return this.getValue('refeicao');
  }

  getDependentes() {
    return this.getValue('dependentes');
  }

  getSalarioLiquido() {
    return (
      this.getSalario() -
      this.getInss() -
      this.getIrrf() -
      this.getOutrosDescontos()
    );
  }

  getLiquidoComplementar() {
    const rendimentos = this.getRemuneracaoTotal() + this.getReembolso();
    const descontos =
      this.getSalario() +
      this.getIrrfComplementar() +
      this.getOutrosDescontos() +
      this.getPat();

    return rendimentos - descontos;
  }

  getTotalLiquido() {
    return (
      this.getSalarioLiquido() + this.getLiquidoComplementar() + this.getPat()
    );
  }

  // Impostos
  getInss() {
    return this.calculadoraInss.calcularDescontoInss(this.getSalario());
  }

  getBaseIrrf() {
    const deducoesLegais =
      this.getInss() + DEDUCAO_DEPENDENTE * this.getDependentes();

    const maiorDeducao = Math.max(deducoesLegais, VALOR_DESCONTO_SIMPLIFICADO);

    return Math.max(0, this.getSalario() - maiorDeducao);
  }

  getIrrf() {
    return this.calculadoraIrrf.calcularIrrf(
      this.getBaseIrrf(),
      this.getSalario()
    );
  }

  getBaseIrrfComplementar() {
    return this.getBaseIrrf() + this.getPida() + this.getPremio();
  }

  getIrrfComplementar() {
    const irrf = this.getIrrf();
    const base = this.getBaseIrrfComplementar();
    const rendimentoTributavel =
      this.getSalario() + this.getPida() + this.getPremio();

    return this.calculadoraIrrf.calcularIrrfComplementar(
      base,
      irrf,
      rendimentoTributavel
    );
  }

  // Custos da empresa
  getPercentualGps() {
    return this.getValue('percentualGps');
  }

  getAssistenciaMedica() {
    return this.getValue('assistenciaMedica');
  }

  getOdontologico() {
    return this.getValue('odontologico');
  }

  getOutrosCustos() {
    return this.getValue('outrosCustos');
  }

  getAlimentacaoRefeicao() {
    return this.getValue('alimentacaoRefeicao');
  }

  getFgts() {
    return this.getSalario() * PERCENTUAL_FGTS;
  }

  getGpsInss() {
    return this.getSalario() * (this.getPercentualGps() / 100);
  }

  getProvisaoDecimoTerceiro() {
    return this.getSalario() * PERCENTUAL_PROVISAO_13_TERCEIRO;
  }

  getEncargosDecimoTerceiro() {
    return (this.getSalario() / 12) * PERCENTUAL_ENCARGOS_13_TERCEIRO;
  }

  getFeriasUmTerco() {
    return this.getSalario() * PERCENTUAL_FERIAS_TERCO;
  }

  getEncargosFeriasUmTerco() {
    return this.getFeriasUmTerco() * PERCENTUAL_ENCARGOS_FERIAS;
  }

  getFgtsRescisao() {
    return this.getFgts() * PERCENTUAL_FGTS_RESCISAO;
  }

  getTotalEncargos() {
    return (
      this.getFgts() +
      this.getGpsInss() +
      this.getProvisaoDecimoTerceiro() +
      this.getEncargosDecimoTerceiro() +
      this.getFeriasUmTerco() +
      this.getEncargosFeriasUmTerco() +
      this.getFgtsRescisao()
    );
  }

  getTotalBeneficios() {
    return (
      this.getAssistenciaMedica() +
      this.getOdontologico() +
      this.getOutrosCustos() +
      this.getAlimentacaoRefeicao()
    );
  }

  getCustoTotal() {
    return (
      this.getTotalEncargos() +
      this.getTotalBeneficios() +
      this.getRemuneracaoTotal()
    );
  }
}
